import useAxios from '@hooks/useFetch';
import { components, paths } from '@api/api';
import { useCallback, useMemo } from "react";

const TOP_TRENDS_API_ENDPOINT: keyof paths = '/TopTrends';
export type TopTrendsRequest = components['schemas']['Application.TopTrends.Models.TopTrendFilterInput'];
export type TopTrendsResponse = components['schemas']['Application.TopTrends.Models.TopTrendResultOutPut'];

type TopTrendsDataItemRaw = NonNullable<NonNullable<TopTrendsResponse>['data']>[number];

export type TopTrendsDataItem = TopTrendsDataItemRaw & {
    penetrationValue: number | null;
    incidenceValue: number | null;
    growthValue: number | null;
    cagrValue: number | null;
    knowItValue: number | null;
    haveTriedItValue: number | null;
    loveOrLikeItValue: number | null;
};

export function getTopTrendsCopyTableData(baseData: string[][], growthPeriod: number): string[][] {
    if (baseData.length === 0) return baseData;

    const formattedDate = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

    const yearPrefix = `${growthPeriod} YEAR${growthPeriod > 1 ? 'S' : ''}`;

    const headerReplacements: Record<string, string> = {
        'PAST GROWTH': `${yearPrefix} PAST GROWTH`,
        'CAGR': `${yearPrefix} CAGR`,
        'GROWTH PERIOD': '',
        'CONSUMERS': '',
    };

    const headerRowCount = baseData.length > 1 && baseData[0].some(c => c === 'GROWTH PERIOD' || c === 'CONSUMERS' || c === '') ? 2 : 1;

    const transformedHeaders = baseData.slice(0, headerRowCount)
        .map(row =>
            row.map(cell => headerReplacements[cell] ?? cell)
        )
        .filter(row => row.some(cell => cell !== ''));

    const dataRows = baseData.slice(headerRowCount);

    return [
        [`Extracted ${formattedDate}`],
        ['Questions? Call us at (312) 655-0622 or email support@datassential.com'],
        [],
        ...transformedHeaders,
        ...dataRows,
    ];
}

export default function useTopTrendsData() {
    const {
        data: rawData,
        isLoading,
        error,
        doFetch,
        cancel,
    } = useAxios<TopTrendsResponse, TopTrendsRequest>({
        method: 'post',
        url: TOP_TRENDS_API_ENDPOINT,
        lazy: true,
    });

    const fetchTopTrends = useCallback(async (requestData: TopTrendsRequest) => {
        await doFetch({ data: requestData });
    }, [doFetch]);

    const transformedData = useMemo((): TopTrendsDataItem[] => {
        if (!rawData?.data) return [];

        const flavorDataArray = rawData.flavorData?.data ?? [];

        return rawData.data.map((item, index) => {
            const flavorItem = flavorDataArray[index];
            return {
                ...item,
                penetrationValue: item.penetration ? parseFloat(item.penetration) : null,
                incidenceValue: item.incidence ? parseFloat(item.incidence) : null,
                growthValue: item.growth ? parseFloat(item.growth) : null,
                cagrValue: item.cagr ? parseFloat(item.cagr) : null,
                knowItValue: flavorItem?.knowIt != null ? flavorItem.knowIt : null,
                haveTriedItValue: flavorItem?.haveTriedIt != null ? flavorItem.haveTriedIt : null,
                loveOrLikeItValue: flavorItem?.loveOrLikeIt != null ? flavorItem.loveOrLikeIt : null,
            };
        });
    }, [rawData]);

    const data = useMemo(() => {
        if (!rawData) return null;
        return {
            ...rawData,
            data: transformedData,
        };
    }, [rawData, transformedData]);

    return {
        data,
        isLoading,
        error,
        doFetch: fetchTopTrends,
        cancel,
    };
}
